import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import { Dialog } from "@mui/material";
import { DialogType } from "app/types";
import { getEnv } from "app/configSlice";
import { AppDispatch, RootState } from "app/store";
import { useDialog, useFetchUserData } from "app/hook";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { ParentCloseBtn, FadeTransition, SpinModal } from "common/elements";
import I18n from "features/parent/i18n";
import { AccountType } from "features/user/types";
import { parentActions } from "features/parent/slice";
import { createChildAccount } from "features/user/api";
import { fetchAccountPlan } from "features/subscription";
import { fetchParentChatRooms } from "features/parent/api";
import { actions as userActions } from "features/user/slice";
import { GenerationStatus } from "features/generation/types";
import { useAIImageGeneration } from "features/generation/hooks/useAIImageGeneration";
import { PlayerStepProgressBar } from "./components";
import { isValidBirthday } from "./steps/PlayerProfileAIStep/BirthdayStep";
import {
  PlayerNameInputStep,
  PlayerLanguageStep,
  PlayerIconMethodStep,
  PlayerPresetIconStep,
  PlayerUploadIconStep,
  PlayerProfileAIStep,
  PlayerPreviewStep,
} from "./steps";
import {
  PlayerWizardStep,
  PlayerIconMethod,
  PlayerProfileSubStep,
  PlayerFavoritesSubStep,
  CreatePlayerState,
  CreatePlayerModalProps,
  PLAYER_WIZARD_STEPS,
  initialPlayerState,
} from "./types";

const MODAL_WIDTH = 900;
const MODAL_HEIGHT = 950;

/** 年齢計算 */
const calculateAge = (birthday: string): number => {
  const birthDate = new Date(birthday);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

/**
 * プロフィールが完全かどうかを判定
 * 全項目が入力されているかチェック
 *
 */
const isProfileComplete = (state: CreatePlayerState): boolean => {
  if (!state.gender || state.gender === "none") return false;
  if (!isValidBirthday(state.birthday)) return false;
  if (!state.accessory || state.accessory.length === 0) return false;
  if (!state.hairstyle) return false;
  if (!state.bodyType) return false;
  if (!state.faceShape) return false;
  if (!state.favoriteColors || state.favoriteColors.length === 0) return false;
  if (!state.bestSubjects || state.bestSubjects.length === 0) return false;
  return true;
};

/** 入力済みステップのIDリストを取得 */
const getCompletedStepIds = (state: CreatePlayerState): string[] => {
  const ids: string[] = [];
  if (state.gender === "male" || state.gender === "female") ids.push("gender");
  if (isValidBirthday(state.birthday)) ids.push("birthday");
  if (state.accessory.length > 0 && state.hairstyle && state.bodyType && state.faceShape)
    ids.push("appearance");
  if (state.favoriteColors.length > 0) ids.push("color");
  if (state.bestSubjects.length > 0) ids.push("bestSubject");
  return ids;
};

export const CreatePlayerModal = ({ open, onClose, onSuccess }: CreatePlayerModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();
  const parent = useSelector((state: RootState) => state.user.parent);
  const users = useSelector((state: RootState) => state.user.users);
  const { handleFetchUserData } = useFetchUserData();

  const [currentStep, setCurrentStep] = useState<PlayerWizardStep>(PlayerWizardStep.NAME_INPUT);
  const [profileSubStep, setProfileSubStep] = useState<PlayerProfileSubStep>(
    PlayerProfileSubStep.GENDER,
  );
  const [state, setState] = useState<CreatePlayerState>(initialPlayerState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [favoritesSubStep, setFavoritesSubStep] = useState<PlayerFavoritesSubStep>(
    PlayerFavoritesSubStep.APPEARANCE,
  );
  const [additionalPrompt, setAdditionalPrompt] = useState<string>("");

  // AI画像生成hook
  const {
    status: generationStatus,
    progress: generationProgress,
    partialImage,
    results: generationResults,
    generate,
    cancel: cancelGeneration,
    reset: resetGeneration,
  } = useAIImageGeneration({});

  // 状態更新
  const updateState = useCallback((updates: Partial<CreatePlayerState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // 生成結果をstateに反映
  useEffect(() => {
    if (generationResults.length > 0 && generationStatus === GenerationStatus.COMPLETE) {
      updateState({ icon: generationResults[0].url });
    }
  }, [generationResults, generationStatus, updateState]);

  // ダイアログを開いた時に初期化
  useEffect(() => {
    if (open) {
      setState({
        ...initialPlayerState,
        name: "",
        locale: "ja",
      });
      setCurrentStep(PlayerWizardStep.NAME_INPUT);
      setProfileSubStep(PlayerProfileSubStep.GENDER);
      setIsSubmitting(false);
      resetGeneration();
      setFavoritesSubStep(PlayerFavoritesSubStep.APPEARANCE);
    }
  }, [open, resetGeneration]);

  // ステップ遷移
  const goToStep = useCallback((step: PlayerWizardStep) => {
    setCurrentStep(step);
  }, []);

  // 次へボタンが有効かどうか
  const canProceed = useCallback(() => {
    switch (currentStep) {
      case PlayerWizardStep.NAME_INPUT:
        return state.name.trim().length > 0 && state.name.length <= 10;
      case PlayerWizardStep.LANGUAGE:
        return !!state.locale;
      case PlayerWizardStep.ICON_METHOD:
        return false;
      case PlayerWizardStep.PRESET_ICON:
        return !!state.icon;
      case PlayerWizardStep.UPLOAD_ICON:
        return !!state.icon;
      case PlayerWizardStep.PROFILE_AI:
        switch (profileSubStep) {
          case PlayerProfileSubStep.GENDER:
            return state.gender === "male" || state.gender === "female";
          case PlayerProfileSubStep.BIRTHDAY:
            return (
              state.birthdayYear !== null &&
              state.birthdayMonth !== null &&
              state.birthdayDay !== null &&
              isValidBirthday(state.birthday)
            );
          case PlayerProfileSubStep.FAVORITES:
            if (favoritesSubStep === PlayerFavoritesSubStep.APPEARANCE) {
              return (
                state.accessory.length > 0 &&
                !!state.hairstyle &&
                !!state.bodyType &&
                !!state.faceShape
              );
            }
            if (favoritesSubStep === PlayerFavoritesSubStep.COLOR) {
              return state.favoriteColors.length > 0;
            }
            if (favoritesSubStep === PlayerFavoritesSubStep.BEST_SUBJECT) {
              return isProfileComplete(state);
            }
            return false;
          case PlayerProfileSubStep.ICON_PREVIEW:
            return generationStatus === GenerationStatus.COMPLETE && !!state.icon;
          default:
            return false;
        }
      case PlayerWizardStep.PREVIEW:
        return true;
      default:
        return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentStep,
    profileSubStep,
    favoritesSubStep,
    state.name,
    state.locale,
    state.icon,
    state.gender,
    state.birthday,
    state.birthdayYear,
    state.birthdayMonth,
    state.birthdayDay,
    state.accessory,
    state.hairstyle,
    state.bodyType,
    state.faceShape,
    state.favoriteColors,
    state.bestSubjects,
    generationStatus,
  ]);

  // 次へ
  const handleNext = useCallback(() => {
    play();
    btnDelay(() => {
      switch (currentStep) {
        case PlayerWizardStep.NAME_INPUT:
          setCurrentStep(PlayerWizardStep.LANGUAGE);
          break;
        case PlayerWizardStep.LANGUAGE:
          setCurrentStep(PlayerWizardStep.ICON_METHOD);
          break;
        case PlayerWizardStep.PRESET_ICON:
        case PlayerWizardStep.UPLOAD_ICON:
          setCurrentStep(PlayerWizardStep.PREVIEW);
          break;
        case PlayerWizardStep.PROFILE_AI:
          switch (profileSubStep) {
            case PlayerProfileSubStep.GENDER:
              setProfileSubStep(PlayerProfileSubStep.BIRTHDAY);
              break;
            case PlayerProfileSubStep.BIRTHDAY:
              setProfileSubStep(PlayerProfileSubStep.FAVORITES);
              setFavoritesSubStep(PlayerFavoritesSubStep.APPEARANCE);
              break;
            case PlayerProfileSubStep.FAVORITES:
              if (favoritesSubStep === PlayerFavoritesSubStep.APPEARANCE) {
                setFavoritesSubStep(PlayerFavoritesSubStep.COLOR);
              } else if (favoritesSubStep === PlayerFavoritesSubStep.COLOR) {
                setFavoritesSubStep(PlayerFavoritesSubStep.BEST_SUBJECT);
              }
              break;
            case PlayerProfileSubStep.ICON_PREVIEW:
              setCurrentStep(PlayerWizardStep.PREVIEW);
              break;
          }
          break;
      }
    });
  }, [currentStep, profileSubStep, favoritesSubStep, play]);

  // 戻る
  const handleBack = useCallback(() => {
    play();
    btnDelay(() => {
      switch (currentStep) {
        case PlayerWizardStep.LANGUAGE:
          setCurrentStep(PlayerWizardStep.NAME_INPUT);
          break;
        case PlayerWizardStep.ICON_METHOD:
          setCurrentStep(PlayerWizardStep.LANGUAGE);
          break;
        case PlayerWizardStep.PRESET_ICON:
        case PlayerWizardStep.UPLOAD_ICON:
          setCurrentStep(PlayerWizardStep.ICON_METHOD);
          break;
        case PlayerWizardStep.PROFILE_AI:
          switch (profileSubStep) {
            case PlayerProfileSubStep.GENDER:
              setCurrentStep(PlayerWizardStep.ICON_METHOD);
              setProfileSubStep(PlayerProfileSubStep.GENDER);
              break;
            case PlayerProfileSubStep.BIRTHDAY:
              setProfileSubStep(PlayerProfileSubStep.GENDER);
              break;
            case PlayerProfileSubStep.FAVORITES:
              if (favoritesSubStep === PlayerFavoritesSubStep.BEST_SUBJECT) {
                setFavoritesSubStep(PlayerFavoritesSubStep.COLOR);
              } else if (favoritesSubStep === PlayerFavoritesSubStep.COLOR) {
                setFavoritesSubStep(PlayerFavoritesSubStep.APPEARANCE);
              } else {
                setProfileSubStep(PlayerProfileSubStep.BIRTHDAY);
              }
              break;
            case PlayerProfileSubStep.ICON_PREVIEW:
              setProfileSubStep(PlayerProfileSubStep.FAVORITES);
              setFavoritesSubStep(PlayerFavoritesSubStep.BEST_SUBJECT);
              setAdditionalPrompt("");
              resetGeneration();
              break;
          }
          break;
        case PlayerWizardStep.PREVIEW:
          if (state.iconMethod === PlayerIconMethod.PRESET) {
            setCurrentStep(PlayerWizardStep.PRESET_ICON);
          } else if (state.iconMethod === PlayerIconMethod.UPLOAD) {
            setCurrentStep(PlayerWizardStep.UPLOAD_ICON);
          } else {
            setCurrentStep(PlayerWizardStep.PROFILE_AI);
            setProfileSubStep(PlayerProfileSubStep.ICON_PREVIEW);
          }
          break;
      }
    });
  }, [currentStep, profileSubStep, favoritesSubStep, state.iconMethod, play, resetGeneration]);

  // AI生成開始
  const handleStartGeneration = useCallback(() => {
    play();
    btnDelay(() => {
      setProfileSubStep(PlayerProfileSubStep.ICON_PREVIEW);
      const age = state.birthday ? calculateAge(state.birthday) : undefined;
      generate({
        gender: (state.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors: state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
        best_subjects: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
        accessory: state.accessory?.length > 0 ? state.accessory : undefined,
        hairstyle: state.hairstyle || undefined,
        body_type: state.bodyType || undefined,
        face_shape: state.faceShape || undefined,
      });
    });
  }, [play, generate, state]);

  // 再生成
  const _handleRegenerate = useCallback(() => {
    play();
    btnDelay(() => {
      resetGeneration();
      updateState({ icon: "" });
      const age = state.birthday ? calculateAge(state.birthday) : undefined;
      generate({
        gender: (state.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors: state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
        best_subjects: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
        accessory: state.accessory?.length > 0 ? state.accessory : undefined,
        hairstyle: state.hairstyle || undefined,
        body_type: state.bodyType || undefined,
        face_shape: state.faceShape || undefined,
      });
    });
  }, [play, generate, resetGeneration, updateState, state]);

  // 追加プロンプト付き再生成
  const handleRegenerateWithPrompt = useCallback(() => {
    play();
    btnDelay(() => {
      resetGeneration();
      updateState({ icon: "" });
      const age = state.birthday ? calculateAge(state.birthday) : undefined;
      generate({
        gender: (state.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors: state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
        best_subjects: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
        accessory: state.accessory?.length > 0 ? state.accessory : undefined,
        hairstyle: state.hairstyle || undefined,
        body_type: state.bodyType || undefined,
        face_shape: state.faceShape || undefined,
        additional_prompt: additionalPrompt || undefined,
      });
    });
  }, [play, generate, resetGeneration, updateState, state, additionalPrompt]);

  // 追加プロンプト変更ハンドラー
  const handleAdditionalPromptChange = useCallback((prompt: string) => {
    setAdditionalPrompt(prompt);
  }, []);

  // 生成中止
  const handleCancelGeneration = useCallback(() => {
    play();
    btnDelay(() => {
      cancelGeneration();
      setAdditionalPrompt("");
      setProfileSubStep(PlayerProfileSubStep.FAVORITES);
      setFavoritesSubStep(PlayerFavoritesSubStep.BEST_SUBJECT);
    });
  }, [play, cancelGeneration]);

  // ユーザー作成確定
  const handleConfirm = useCallback(async () => {
    play();
    btnDelay(async () => {
      setIsSubmitting(true);
      handleDialog({ type: DialogType.LOADING, value: true });
      try {
        const iconMetadata =
          state.iconMethod === PlayerIconMethod.PROFILE_AI && state.icon
            ? {
                source: "ai_generated" as const,
                file_size: generationResults[0]?.fileSize || 0,
              }
            : state.iconMethod === PlayerIconMethod.UPLOAD && state.icon
              ? state.iconMetadata
              : undefined;

        const result = await dispatch(
          createChildAccount({
            user: {
              parent_uid: parent!.uid,
              display_name: state.name,
              locale: state.locale,
              env: getEnv(),
              icon: state.icon || undefined,
              birthday: state.birthday || undefined,
              gender: state.gender || undefined,
              favorite_color: state.favoriteColors?.length > 0 ? state.favoriteColors : undefined,
              best_subject: state.bestSubjects?.length > 0 ? state.bestSubjects : undefined,
              accessory: state.accessory?.length > 0 ? state.accessory : undefined,
              hairstyle: state.hairstyle || undefined,
              body_type: state.bodyType || undefined,
              face_shape: state.faceShape || undefined,
              icon_metadata: iconMetadata,
            },
          }),
        ).unwrap();

        toast(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_CREATE_PLAYER_SUCCESS")}</p>
          </div>,
        );

        // 新しいチャットルームを取得して差分追加
        try {
          const chatRooms = await fetchParentChatRooms({ uid: parent!.uid });
          dispatch(parentActions.addParentChatRooms(chatRooms));
        } catch (error) {
          console.error("Failed to fetch chat rooms:", error);
        }

        // limitsを再取得（新しい子供のlimitsを含む）
        dispatch(fetchAccountPlan({ uid: parent!.uid }));

        // プレイヤー0人から追加した場合のみ、active設定とデータフェッチ
        // users配列には親も含まれるため、子どもの数をカウント
        const childCount = users.filter((u) => u.account_type !== AccountType.PARENTS).length;
        if (childCount === 0) {
          dispatch(userActions.setActiveUser(result));
          // 新規プレイヤーのデータをフェッチ
          await handleFetchUserData({
            parent_uid: parent!.uid,
            uid: result.uid,
          });
        }

        if (onSuccess) {
          onSuccess(result);
        } else {
          onClose();
        }
      } catch (error) {
        console.error("Failed to create player:", error);
        toast(
          <div>
            <p className="text text-textcolor-black">{I18n.t("MSG_ERROR_NETWORK")}</p>
          </div>,
        );
      } finally {
        setIsSubmitting(false);
        handleDialog({ type: DialogType.LOADING, value: false });
      }
    });
  }, [
    state,
    parent,
    users,
    handleFetchUserData,
    dispatch,
    onClose,
    onSuccess,
    play,
    generationResults,
    handleDialog,
  ]);

  // キャンセル
  const handleCancel = useCallback(() => {
    play();
    btnDelay(() => {
      onClose();
    });
  }, [onClose, play]);

  // サブステップクリック時の処理
  const handleSubStepClick = useCallback(
    (targetSubStep: PlayerProfileSubStep, targetFavoritesSubStep?: PlayerFavoritesSubStep) => {
      // 生成中はクリック無効
      if (generationStatus === GenerationStatus.GENERATING) {
        return;
      }
      play();
      setProfileSubStep(targetSubStep);
      if (targetFavoritesSubStep) {
        setFavoritesSubStep(targetFavoritesSubStep);
      } else if (targetSubStep !== PlayerProfileSubStep.FAVORITES) {
        // FAVORITES以外のステップに移動する場合、favoritesSubStepを初期値にリセット
        setFavoritesSubStep(PlayerFavoritesSubStep.APPEARANCE);
      }
    },
    [play, generationStatus],
  );

  // ステップに応じたコンテンツをレンダリング
  const renderContent = () => {
    const stepProps = { state, updateState };

    switch (currentStep) {
      case PlayerWizardStep.NAME_INPUT:
        return <PlayerNameInputStep {...stepProps} />;
      case PlayerWizardStep.LANGUAGE:
        return <PlayerLanguageStep {...stepProps} />;
      case PlayerWizardStep.ICON_METHOD:
        return <PlayerIconMethodStep {...stepProps} goToStep={goToStep} />;
      case PlayerWizardStep.PRESET_ICON:
        return <PlayerPresetIconStep {...stepProps} />;
      case PlayerWizardStep.UPLOAD_ICON:
        return <PlayerUploadIconStep {...stepProps} parentUid={parent!.uid} />;
      case PlayerWizardStep.PROFILE_AI:
        return (
          <PlayerProfileAIStep
            state={state}
            updateState={updateState}
            currentSubStep={profileSubStep}
            generationStatus={generationStatus}
            generationProgress={generationProgress}
            generatedImage={state.icon || null}
            partialImage={partialImage}
            favoritesSubStep={favoritesSubStep}
            onSubStepNext={handleNext}
            onSubStepClick={handleSubStepClick}
            additionalPrompt={additionalPrompt}
            onAdditionalPromptChange={handleAdditionalPromptChange}
            onRegenerate={handleRegenerateWithPrompt}
            completedStepIds={getCompletedStepIds(state)}
            isGenerating={generationStatus === GenerationStatus.GENERATING}
          />
        );
      case PlayerWizardStep.PREVIEW:
        return <PlayerPreviewStep state={state} />;
      default:
        return null;
    }
  };

  // フッターボタン
  const renderFooter = () => {
    const isProfileAIStep = currentStep === PlayerWizardStep.PROFILE_AI;
    const isIconPreviewSubStep =
      isProfileAIStep && profileSubStep === PlayerProfileSubStep.ICON_PREVIEW;
    const isGenerating = generationStatus === GenerationStatus.GENERATING;

    // 生成中は中止ボタンのみ
    if (isIconPreviewSubStep && isGenerating) {
      return (
        <div className="flex-row-el flex-center gap-4 mt-6">
          <button
            onClick={handleCancelGeneration}
            className="flex-row-el items-center justify-center w-[200px] py-3 bg-danger text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
          >
            {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
          </button>
        </div>
      );
    }

    // 生成完了時は2ボタン（戻る＋決定）
    if (isIconPreviewSubStep && !isGenerating) {
      const nextDisabled = !canProceed();
      return (
        <div className="flex-row-el flex-center gap-4 mt-6">
          <button
            onClick={handleBack}
            className="flex-row-el items-center justify-center w-[200px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
          >
            {I18n.t("MSG_BACK_BTN_BY_PARENT")}
          </button>
          <button
            onClick={handleNext}
            disabled={nextDisabled}
            className="flex-row-el items-center justify-center w-[200px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {I18n.t("MSG_ICON_CONFIRM_BTN_BY_PARENT")}
          </button>
        </div>
      );
    }

    const isConfirmStep = currentStep === PlayerWizardStep.PREVIEW;
    const isPreGenerateSubStep =
      isProfileAIStep &&
      profileSubStep === PlayerProfileSubStep.FAVORITES &&
      favoritesSubStep === PlayerFavoritesSubStep.BEST_SUBJECT;

    const showBackButton = currentStep !== PlayerWizardStep.NAME_INPUT;
    const showNextButton = currentStep !== PlayerWizardStep.ICON_METHOD;
    const nextDisabled = !canProceed();

    let nextLabel = I18n.t("MSG_CREATE_USER_BTN_NEXT_BY_PARENT");
    let onNextClick = handleNext;

    if (isConfirmStep) {
      nextLabel = I18n.t("MSG_CONFIRM_BTN_BY_PARENT");
      onNextClick = handleConfirm;
    } else if (isPreGenerateSubStep) {
      nextLabel = I18n.t("MSG_CREATE_USER_BTN_GENERATE_BY_PARENT");
      onNextClick = handleStartGeneration;
    }

    return (
      <div className="flex-row-el flex-center gap-4 mt-6">
        {showBackButton && (
          <button
            onClick={handleBack}
            className="flex-row-el items-center justify-center w-[200px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
          >
            {I18n.t("MSG_BACK_BTN_BY_PARENT")}
          </button>
        )}
        {showNextButton && (
          <button
            onClick={onNextClick}
            disabled={nextDisabled}
            className="flex-row-el items-center justify-center w-[200px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {nextLabel}
          </button>
        )}
      </div>
    );
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        {isSubmitting && <SpinModal />}
        <div style={{ transform: `scale(${scale})` }}>
          <div
            className="flex-col-el shrink-0 border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30"
            style={{ width: MODAL_WIDTH, height: MODAL_HEIGHT }}
          >
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={handleCancel} />
              {/* ヘッダー */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">
                  person_add
                </span>
                <h2 className="text-[33px] text-textcolor-black">
                  {I18n.t("MSG_PARENT_PAGE_CREATE_PLAYER_TITLE")}
                </h2>
              </div>

              {/* ステップインジケータ */}
              <PlayerStepProgressBar
                steps={PLAYER_WIZARD_STEPS}
                currentStep={currentStep}
                onStepClick={goToStep}
              />

              {/* コンテンツ */}
              <div className="flex-col-el flex-1 w-full overflow-hidden">{renderContent()}</div>

              {/* フッター */}
              {renderFooter()}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
